import moment from "moment";
import i18n from "../../../plugins/i18n";
import {
  deepCloneObject,
  getLocalisedValue,
} from "@/app/core-module/util/coreHelper";

export const getTrainingModuleContentsCountText = trainingModule => {
  // 4 Videos, 2 Readings, 3 Audios, 2 Quizzes
  let countTextList = [],
    videos = 0,
    readings = 0,
    audios = 0,
    quizzes = 0;

  if (
    trainingModule &&
    trainingModule.trainingModuleContents &&
    trainingModule.trainingModuleContents.length
  ) {
    for (const trainingModuleContent of trainingModule.trainingModuleContents) {
      if (trainingModuleContent.contentType === "VIDEO") videos += 1;
      else if (trainingModuleContent.contentType === "TEXT") readings += 1;
      else if (trainingModuleContent.contentType === "AUDIO") audios += 1;
      else if (trainingModuleContent.contentType === "QUIZ") quizzes += 1;
    }
  }

  if (videos > 0)
    countTextList.push(`${videos} ${i18n.t("course.courseDetails.videos")}`);

  if (readings > 0)
    countTextList.push(
      `${readings} ${i18n.t("course.courseDetails.readings")}`
    );

  if (audios > 0)
    countTextList.push(`${audios} ${i18n.t("course.courseDetails.audios")}`);

  if (quizzes > 0)
    countTextList.push(`${quizzes} ${i18n.t("course.courseDetails.quizzes")}`);

  return countTextList.join(", ");
};

export const getTrainingModuleContentTypeIcon = trainingModuleContent => {
  if (trainingModuleContent.contentType === "VIDEO") return "mdi-video-outline";
  else if (trainingModuleContent.contentType === "TEXT")
    return "mdi-note-text-outline";
  else if (trainingModuleContent.contentType === "AUDIO")
    return "mdi-headphones";
  else if (trainingModuleContent.contentType === "QUIZ")
    return "mdi-message-question-outline";
};

export const getTrainingModuleContentTitle = (
  trainingModuleContent,
  locale
) => {
  if (trainingModuleContent?.contentType === "QUIZ")
    return getLocalisedValue(trainingModuleContent?.quizContent?.title, {
      preferredLocale: locale,
    });

  return getLocalisedValue(trainingModuleContent?.title, {
    preferredLocale: locale,
  });
};

// used in EMPLOYEE
export const getTrainingProgressPercentage = (
  trainingRegistration,
  upcoming = false
) => {
  try {
    if (trainingRegistration?.status === "COMPLETED") return 100;

    const savedProgress = trainingRegistration.trainingProgressPercentage ?? 0;

    const trainingProgressesCount =
      trainingRegistration.trainingProgresses.filter(
        x => x.status === "COMPLETED"
      ).length + (upcoming ? 1 : 0);

    const trainingModuleContentsCount =
      trainingRegistration.training.trainingModules.reduce(
        (count, item) => count + item.trainingModuleContents.length,
        0
      );

    const calculatedProgress = parseInt(
      ((trainingProgressesCount / trainingModuleContentsCount) * 100).toFixed(0)
    );

    return Math.max(savedProgress, calculatedProgress);
  } catch (e) {
    console.log("courseHelper.getTrainingProgressPercentage:", e.message);
    return 0;
  }
};

// used in ADMIN
export const validateTraining = training => {
  try {
    training.hasInvalidTitle = !training.title?.find(
      x => x.value?.trim()?.length
    );

    training.feedback?.questionAnswers?.forEach(item => {
      item.hasInvalidQuestion = !item.question?.find(
        x => x.value?.trim()?.length
      );
    });

    training.trainingModules?.forEach(tm => {
      tm.hasInvalidTitle = !tm.title?.find(x => x.value?.trim()?.length);

      tm?.trainingModuleContents?.forEach(tmc => {
        if (tmc.contentType === "QUIZ") {
          tmc.quiz?.questionAnswers?.forEach(item => {
            item.hasInvalidQuestion = !item.question?.find(
              x => x.value?.trim()?.length
            );

            item.choices?.forEach(ch => {
              ch.hasInvalidChoice = !ch.value?.find(
                x => x.value?.trim()?.length
              );
            });

            item.hasInvalidChoices = item.choices?.some(
              ch => ch.hasInvalidChoice
            );
          });
        } else {
          tmc.hasInvalidTitle = !tmc.title?.find(x => x.value?.trim()?.length);
        }
      });
    });
  } catch (e) {
    console.log("courseHelper.validateTraining:", e.message);
  }

  return training;
};

// used in ADMIN
export const sortTrainingModulesInTraining = trainingOg => {
  try {
    const training = deepCloneObject(trainingOg);

    training.feedback?.questionAnswers?.sort(
      (a, b) => a.displayPosition - b.displayPosition
    );

    training.trainingModules?.sort(
      (a, b) => a.displayPosition - b.displayPosition
    );

    training.trainingModules?.forEach(tm => {
      tm.trainingModuleContents?.sort(
        (a, b) => a.displayPosition - b.displayPosition
      );

      tm.trainingModuleContents?.forEach(tmc => {
        tmc.quiz?.questionAnswers?.sort(
          (a, b) => a.displayPosition - b.displayPosition
        );
      });
    });

    return training;
  } catch (e) {
    console.log("courseHelper.sortTrainingModulesInTraining:", e.message);
  }

  return trainingOg;
};

// used in EMPLOYEE
export const filterTrainingModulesInTrainingRegistration =
  trainingRegistrationOg => {
    try {
      //TODO:TRAINING: if a training have, updated or deleted module content
      if (
        trainingRegistrationOg.status === "COMPLETED" &&
        trainingRegistrationOg.sortedTrainingModules?.length
      ) {
        const trainingRegistration = deepCloneObject(trainingRegistrationOg);

        const filteredTrainingModules = [];
        const filteredTrainingModuleContentIds = [];

        trainingRegistration.sortedTrainingModules =
          trainingRegistration.sortedTrainingModules.reduce(
            (tmResult, sortedTrainingModule) => {
              const existingTrainingModule =
                trainingRegistration.training?.trainingModules?.find(
                  etm => etm._id === sortedTrainingModule.trainingModule?._id
                );

              if (existingTrainingModule) {
                const filteredTrainingModuleContents = [];

                sortedTrainingModule.trainingModuleContents =
                  sortedTrainingModule.trainingModuleContents?.reduce(
                    (tmcResult, sortedTrainingModuleContent) => {
                      const existingTrainingModuleContent =
                        existingTrainingModule.trainingModuleContents?.find(
                          etmc => etmc._id === sortedTrainingModuleContent?._id
                        );

                      if (existingTrainingModuleContent) {
                        filteredTrainingModuleContents.push(
                          existingTrainingModuleContent
                        );

                        filteredTrainingModuleContentIds.push(
                          existingTrainingModuleContent._id
                        );

                        tmcResult.push(sortedTrainingModuleContent);
                      }

                      return tmcResult;
                    },
                    []
                  );

                existingTrainingModule.trainingModuleContents =
                  filteredTrainingModuleContents;

                filteredTrainingModules.push(existingTrainingModule);

                tmResult.push(sortedTrainingModule);
              }

              return tmResult;
            },
            []
          );

        if (trainingRegistration.training) {
          trainingRegistration.training.trainingModules =
            filteredTrainingModules;
        }

        trainingRegistration.trainingProgresses =
          trainingRegistration.trainingProgresses?.filter(tp =>
            filteredTrainingModuleContentIds.includes(
              tp?.trainingModuleContent?._id
            )
          );

        return trainingRegistration;
      }
    } catch (e) {
      console.log(
        "courseHelper.filterTrainingModulesInTrainingRegistration:",
        e.message
      );
    }

    return trainingRegistrationOg;
  };

// used in ADMIN
export const generateCertificateExpiryDate = trainingRegistration => {
  try {
    const expiresAt = Number(trainingRegistration?.trainingCertificate?.expiresAt);
    const certificateValidity = Number(trainingRegistration?.certificateValidity);

    if (
      trainingRegistration?.trainingCertificate?.expiresAt != null &&
      expiresAt === 0
    ) {
      return "N/A";
    }

    if (
      trainingRegistration?.trainingMode === "OFFLINE" &&
      certificateValidity === 0
    ) {
      return "N/A";
    }

    if (trainingRegistration?.trainingCertificate?.expiresAt) {
      return moment(expiresAt).format("DD MMM YYYY");
    }

    if (
      trainingRegistration?.trainingMode === "OFFLINE" &&
      trainingRegistration?.endDate &&
      certificateValidity > 0
    ) {
      return moment(Number(trainingRegistration.endDate))
        .add(certificateValidity, "days")
        .format("DD MMM YYYY");
    }
  } catch (e) {
    console.log("courseHelper.generateCertificateExpiryDate:", e.message);
  }

  return "-";
};
